import { useCallback, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import type { CategoryRow } from '@/db/types'
import {
  archiveCategory,
  countEntriesForCategory,
  deleteCategory,
  updateCategory,
} from '@/core/repository/catalogRepository'
import { db } from '@/db/schema'
import { logger } from '@/core/logging/logger'
import { ColorField } from './ColorField'

interface CategoryEditorProps {
  category: CategoryRow
  onClose: () => void
  onChanged: () => void
}

/**
 * Editing one category.
 *
 * The category colour is only the heading dot in the picker -- blocks take
 * their colour from the event type. Said plainly here, because choosing a
 * category colour and expecting the blocks to change is the obvious mistake.
 */
export function CategoryEditor({ category, onClose, onChanged }: CategoryEditorProps) {
  const [name, setName] = useState(category.name)
  const [colorHex, setColorHex] = useState(category.colorHex)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const typeCount = useLiveQuery(
    () =>
      db.eventTypes
        .where('categoryId')
        .equals(category.id)
        .filter((t) => t.deletedAt === null)
        .count(),
    [category.id],
    0,
  )
  const blockCount = useLiveQuery(() => countEntriesForCategory(category.id), [category.id], 0)

  const save = useCallback(async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('A category needs a name.')
      return
    }
    await updateCategory(category.id, { name: trimmed, colorHex })
    onChanged()
    onClose()
  }, [name, colorHex, category.id, onChanged, onClose])

  const handleArchive = useCallback(async () => {
    await archiveCategory(category.id, category.isArchived === 0)
    onChanged()
    onClose()
  }, [category.id, category.isArchived, onChanged, onClose])

  const handleDelete = useCallback(async () => {
    try {
      await deleteCategory(category.id)
      onChanged()
      onClose()
    } catch (err: unknown) {
      logger.error('db', 'Could not delete category', err)
      setError('Could not delete that category.')
    }
  }, [category.id, onChanged, onClose])

  return (
    <div className="mg-sheet mg-sheet--over" role="dialog" aria-label={`Edit ${category.name}`}>
      <header className="mg-head">
        <button className="mg-head-btn" onClick={onClose}>
          Cancel
        </button>
        <h2 className="mg-title">Category</h2>
        <button className="mg-head-btn mg-head-btn--strong" onClick={() => void save()}>
          Save
        </button>
      </header>

      {error ? (
        <p className="mg-status mg-status--error" role="status">
          {error}
        </p>
      ) : null}

      <div className="mg-body scroll-y">
        <section className="mg-section">
          <label className="mg-field-label" htmlFor="mg-cat-name">
            Name
          </label>
          <input
            id="mg-cat-name"
            className="mg-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Work"
          />
        </section>

        <section className="mg-section">
          <h3 className="mg-label">Colour</h3>
          <p className="mg-hint">
            This is the dot beside the heading in the picker. Blocks take their colour from the
            event type, not from here.
          </p>
          <ColorField value={colorHex} onChange={setColorHex} sampleText={name || 'Work'} />
        </section>

        <section className="mg-section">
          <h3 className="mg-label">Removing it</h3>
          <p className="mg-hint">
            {typeCount} event type{typeCount === 1 ? '' : 's'}, {blockCount} block
            {blockCount === 1 ? '' : 's'} tracked.
          </p>

          <button className="mg-btn" onClick={() => void handleArchive()}>
            {category.isArchived === 1 ? 'Bring back into the picker' : 'Archive'}
          </button>

          <button
            className="mg-btn mg-btn--danger"
            onClick={() => (confirmDelete ? void handleDelete() : setConfirmDelete(true))}
          >
            {confirmDelete ? 'Tap again to delete' : 'Delete'}
          </button>
          <p className="mg-hint">
            Deleting a category never deletes its event types or your history. They move to
            Uncategorised and keep working.
          </p>
        </section>
      </div>
    </div>
  )
}
